import { type FetchResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { notify } from 'src/lib/notify';
import type {
  CreateDocument,
  CreateDocumentInput,
  CreateDocumentVariables,
  DocumentParentInput,
  deleteDocument,
  deleteDocumentVariables,
} from 'types/graphql';

const CREATE_DOCUMENT = gql`
  mutation CreateDocument($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      id
      title
      url
      type
      createdAt
      uploadedBy {
        user {
          id
          firstName
          lastName
        }
      }
      documentTemplateId
      documentTemplate {
        name
      }
    }
  }
`;

const DELETE_DOCUMENT_MUTATION = gql`
  mutation deleteDocument($id: Int!) {
    deleteDocument(id: $id) {
      id
    }
  }
`;

type DocumentParentKey = keyof DocumentParentInput;

const DOCUMENT_PARENT_ON: { [K in DocumentParentKey]: (id: number) => DocumentParentInput } = {
  billId: (billId) => ({ billId }),
  buySellOrderId: (buySellOrderId) => ({ buySellOrderId }),
  lotId: (lotId) => ({ lotId }),
  productionRunId: (productionRunId) => ({ productionRunId }),
  purchaseOrderId: (purchaseOrderId) => ({ purchaseOrderId }),
  salesOrderId: (salesOrderId) => ({ salesOrderId }),
  workOrderId: (workOrderId) => ({ workOrderId }),
};

const isDocumentParentKey = (key: string): key is DocumentParentKey => Object.hasOwn(DOCUMENT_PARENT_ON, key);

/** The hook's call shape, unchanged: the owner id beside `input`, as the deprecated argument list had it. */
export type CreateDocumentCall = { [K in DocumentParentKey]?: number | null } & {
  input: Omit<CreateDocumentInput, 'parent'>;
};

/** Moves the one owner id into `input.parent` (TAS-3660 N9); keys outside the parent set were never sent. */
const toCreateDocumentVariables = ({ input, ...ownerIds }: CreateDocumentCall): CreateDocumentVariables => {
  const parents = Object.entries(ownerIds).flatMap(([key, id]) =>
    isDocumentParentKey(key) && typeof id === 'number' ? [DOCUMENT_PARENT_ON[key](id)] : []
  );
  if (parents.length > 1) throw new Error('Choose one parent for the document.');

  const [parent] = parents;
  return { input: parent ? { ...input, parent } : input };
};

/** `createDocument` (P7-R2). `silent` skips the "Document uploaded" toast, as before. */
export function useCreateDocument(silent = false) {
  const [mutate, { loading, error }] = useMutation<CreateDocument, CreateDocumentVariables>(CREATE_DOCUMENT, {
    onCompleted: () => {
      if (!silent) notify.success('Document uploaded');
    },
  });
  const create = useCallback(
    async (call: CreateDocumentCall): Promise<FetchResult<CreateDocument>> =>
      mutate({ variables: toCreateDocumentVariables(call) }),
    [mutate]
  );
  return { create, loading, error };
}

/** `deleteDocument` (P7-R2). Soft-deletes one document; the operation name stays lowercase (telemetry). */
export function useDeleteDocument() {
  const [mutate, { loading, error }] = useMutation<deleteDocument, deleteDocumentVariables>(DELETE_DOCUMENT_MUTATION);
  const remove = useCallback((id: number) => mutate({ variables: { id } }), [mutate]);
  return { remove, loading, error };
}
